/**
 * 表格插入对话框
 * 支持手动创建表格 + AI 自动生成
 */
import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface TableData {
  rows: number;
  cols: number;
  data: string[][];
  headerColor?: string;
  textColor?: string;
}

interface TableDialogProps {
  show: boolean;
  onClose: () => void;
  onInsert: (tableData: TableData) => void;
  contextText?: string;
  selectedModel?: string;
}

// 颜色渐变方案（6种颜色，每种5个深浅，浅色在上深色在下）
const COLOR_GRADIENTS = [
  // 蓝色系（从浅到深）
  ['#EFF6FF', '#DBEAFE', '#BFDBFE', '#60A5FA', '#3B82F6'],
  // 绿色系
  ['#F0FDF4', '#DCFCE7', '#BBF7D0', '#4ADE80', '#10B981'],
  // 橙色系
  ['#FFF7ED', '#FFEDD5', '#FED7AA', '#FB923C', '#F97316'],
  // 紫色系
  ['#FAF5FF', '#F3E8FF', '#E9D5FF', '#A78BFA', '#8B5CF6'],
  // 红色系
  ['#FEF2F2', '#FEE2E2', '#FECACA', '#F87171', '#EF4444'],
  // 灰色系
  ['#F9FAFB', '#F3F4F6', '#D1D5DB', '#9CA3AF', '#6B7280'],
];

// 文字颜色选项（包括纯黑色）
const TEXT_COLORS = [
  { name: '纯黑', color: '#000000' },
  { name: '白色', color: '#FFFFFF' },
  { name: '深灰', color: '#1F2937' },
  { name: '中灰', color: '#6B7280' },
  { name: '浅灰', color: '#9CA3AF' },
  { name: '蓝色', color: '#3B82F6' },
  { name: '绿色', color: '#10B981' },
  { name: '橙色', color: '#F97316' },
  { name: '紫色', color: '#8B5CF6' },
  { name: '红色', color: '#EF4444' },
];

const TableDialog: React.FC<TableDialogProps> = ({
  show,
  onClose,
  onInsert,
  contextText = '',
  selectedModel = '',
}) => {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [selectedColor, setSelectedColor] = useState('#3B82F6');
  const [selectedTextColor, setSelectedTextColor] = useState('#FFFFFF');
  const [isGenerating, setIsGenerating] = useState(false);
  const [tableData, setTableData] = useState<string[][]>([]);
  const [error, setError] = useState<string>('');
  const [isAnimating, setIsAnimating] = useState(false);

  // 下拉面板状态
  const [showRowColPanel, setShowRowColPanel] = useState(false);
  const [showColorPanel, setShowColorPanel] = useState(false);
  const [showTextColorPanel, setShowTextColorPanel] = useState(false);
  const [hoverRow, setHoverRow] = useState(0);
  const [hoverCol, setHoverCol] = useState(0);

  const rowColPanelRef = useRef<HTMLDivElement>(null);
  const colorPanelRef = useRef<HTMLDivElement>(null);
  const textColorPanelRef = useRef<HTMLDivElement>(null);

  // 动画效果
  useEffect(() => {
    if (show) {
      setIsAnimating(true);
    } else {
      setIsAnimating(false);
    }
  }, [show]);

  // 如果有选中文字，自动调用 AI 生成
  useEffect(() => {
    if (show && contextText && contextText.trim()) {
      generateTableFromText(contextText);
    } else if (show) {
      initEmptyTable(3, 3);
    }
  }, [show, contextText]);

  // 点击外部关闭下拉面板
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rowColPanelRef.current && !rowColPanelRef.current.contains(event.target as Node)) {
        setShowRowColPanel(false);
      }
      if (colorPanelRef.current && !colorPanelRef.current.contains(event.target as Node)) {
        setShowColorPanel(false);
      }
      if (textColorPanelRef.current && !textColorPanelRef.current.contains(event.target as Node)) {
        setShowTextColorPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 初始化空表格
  const initEmptyTable = (r: number, c: number) => {
    const data: string[][] = [];
    for (let i = 0; i < r; i++) {
      const row: string[] = [];
      for (let j = 0; j < c; j++) {
        if (i === 0) {
          row.push(`列${j + 1}`);
        } else {
          row.push('');
        }
      }
      data.push(row);
    }
    setTableData(data);
  };

  // AI 生成表格
  const generateTableFromText = async (text: string) => {
    setIsGenerating(true);
    setError('');

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      const url = `${API_BASE_URL}/api/content/generate-table`;

      console.log('🔷 开始生成表格');
      console.log('  API URL:', url);
      console.log('  文本长度:', text.length);
      console.log('  文本内容（前200字）:', text.substring(0, 200));

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('access_token') ? { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } : {}),
        },
        body: JSON.stringify({
          text,
          model: selectedModel || undefined
        }),
      });

      console.log('  响应状态:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('  响应错误:', errorText);
        // 尝试解析 JSON 错误消息
        let errorMessage = `生成失败: ${response.status} ${errorText}`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.detail) errorMessage = errorData.detail;
        } catch { /* 就用原文本 */ }
        // Token 欠费：触发全局欠费弹框事件
        if (response.status === 400 && errorMessage.includes('Token') &&
            (errorMessage.includes('欠费') || errorMessage.includes('不足') || errorMessage.includes('额度'))) {
          window.dispatchEvent(new CustomEvent('token:overdraft', { detail: { message: errorMessage } }));
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('  返回数据:', data);

      if (data.table_data && Array.isArray(data.table_data)) {
        setTableData(data.table_data);
        setRows(data.table_data.length);
        setCols(data.table_data[0]?.length || 0);
        console.log('✅ 表格生成成功:', data.table_data.length, '行', data.table_data[0]?.length, '列');
      } else {
        throw new Error('返回数据格式错误');
      }
    } catch (error) {
      console.error('❌ 生成表格失败:', error);
      const errorMsg = (error as Error)?.message || '';
      const isTokenError = errorMsg.includes('Token') &&
          (errorMsg.includes('欠费') || errorMsg.includes('不足') || errorMsg.includes('额度'));
      if (isTokenError) {
        // Token 欠费：已触发全局弹框，不显示 UI 错误提示
      } else if (error instanceof TypeError && errorMsg === 'Failed to fetch') {
        setError('无法连接到后端服务，请检查后端是否启动');
      } else {
        setError('AI 生成失败，请手动创建表格');
      }
      initEmptyTable(3, 3);
    } finally {
      setIsGenerating(false);
    }
  };

  // 更新单元格内容
  const handleCellChange = (rowIndex: number, colIndex: number, value: string) => {
    const data = [...tableData];
    data[rowIndex][colIndex] = value;
    setTableData(data);
  };

  // 处理行列网格选择
  const handleGridSelect = (r: number, c: number) => {
    setRows(r);
    setCols(c);
    setShowRowColPanel(false);

    // 调整表格数据
    const data: string[][] = [];
    for (let i = 0; i < r; i++) {
      const row: string[] = [];
      for (let j = 0; j < c; j++) {
        const existingValue = tableData[i]?.[j];
        if (existingValue !== undefined) {
          row.push(existingValue);
        } else if (i === 0) {
          row.push(`列${j + 1}`);
        } else {
          row.push('');
        }
      }
      data.push(row);
    }
    setTableData(data);
  };

  // 插入表格
  const handleInsert = () => {
    if (tableData.length === 0) return;

    onInsert({
      rows,
      cols,
      data: tableData,
      headerColor: selectedColor,
      textColor: selectedTextColor,
    });
    handleClose();
  };

  const handleClose = () => {
    setRows(3);
    setCols(3);
    setSelectedColor('#3B82F6');
    setSelectedTextColor('#FFFFFF');
    setTableData([]);
    setError('');
    setShowRowColPanel(false);
    setShowColorPanel(false);
    setShowTextColorPanel(false);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[200] overflow-y-auto">
      {/* 强制覆盖外部CSS污染 - 与样式设置弹框完全一致 */}
      <style>{`
        div.table-dialog-header,
        div.table-dialog-header * {
          line-height: normal !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
        }
        div.table-dialog-header h3 {
          font-size: 16px !important;
          line-height: 1.5 !important;
          color: #ffffff !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        #table-dialog-desc,
        p#table-dialog-desc,
        div.table-dialog-header p#table-dialog-desc {
          font-size: 10px !important;
          line-height: 1.2 !important;
          color: rgba(191, 219, 254, 1) !important;
          margin: 0 !important;
          margin-top: 2px !important;
          padding: 0 !important;
        }
      `}</style>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* 背景遮罩 - 带动画 */}
        <div
          className={`fixed inset-0 bg-gray-900 transition-all duration-300 ${
            isAnimating ? 'bg-opacity-60 backdrop-blur-sm' : 'bg-opacity-0'
          }`}
          onClick={handleClose}
        ></div>

        {/* 对话框 - 带动画 */}
        <div
          className={`inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all duration-300 sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full ${
            isAnimating ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          {/* 头部 - 与样式设置弹框保持一致 */}
          <div className="table-dialog-header bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm">
                <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-white">插入表格</h3>
                <p id="table-dialog-desc" className="text-[10px] text-blue-100">创建或AI生成表格</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white/90 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition-all duration-200"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {isGenerating ? (
            <div className="flex items-center justify-center p-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-sm text-gray-600">AI正在智能生成表格...</p>
              </div>
            </div>
          ) : (
            <>
              {/* 配置区域 - 紧凑单行 */}
              <div className="bg-white px-5 py-2 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  {/* 行列选择 */}
                  <div className="relative flex items-center gap-2" ref={rowColPanelRef}>
                    <label className="text-xs font-medium text-gray-600 whitespace-nowrap">行列数</label>
                    <button
                      onClick={() => setShowRowColPanel(!showRowColPanel)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 cursor-pointer flex items-center gap-2"
                    >
                      <span>{rows} × {cols}</span>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showRowColPanel && (
                      <div className="absolute top-full mt-1 left-0 bg-white border border-gray-300 rounded-lg shadow-xl p-2 z-50">
                        <div className="text-xs text-gray-600 mb-1.5 text-center">
                          {hoverRow || rows} 行 × {hoverCol || cols} 列
                        </div>
                        <div className="grid gap-0.5" style={{ gridTemplateColumns: 'repeat(10, 1fr)' }}>
                          {Array.from({ length: 100 }, (_, index) => {
                            const row = Math.floor(index / 10) + 1;
                            const col = (index % 10) + 1;
                            const isSelected = row <= (hoverRow || rows) && col <= (hoverCol || cols);
                            return (
                              <div
                                key={index}
                                className={`w-4 h-4 border transition-colors cursor-pointer ${
                                  isSelected ? 'bg-blue-500 border-blue-600' : 'bg-white border-gray-300 hover:bg-gray-100'
                                }`}
                                onMouseEnter={() => { setHoverRow(row); setHoverCol(col); }}
                                onMouseLeave={() => { setHoverRow(0); setHoverCol(0); }}
                                onClick={() => handleGridSelect(row, col)}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 表头颜色 */}
                  <div className="relative flex items-center gap-2" ref={colorPanelRef}>
                    <label className="text-xs font-medium text-gray-600 whitespace-nowrap">表头</label>
                    <button
                      onClick={() => setShowColorPanel(!showColorPanel)}
                      className="px-2 py-1 border border-gray-300 rounded-lg bg-white hover:border-gray-400 cursor-pointer flex items-center gap-1.5"
                    >
                      <div className="w-5 h-5 rounded border border-gray-300" style={{ backgroundColor: selectedColor }} />
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showColorPanel && (
                      <div className="absolute top-full mt-1 left-0 bg-white border border-gray-300 rounded-lg shadow-xl p-2 z-50">
                        <div className="space-y-1">
                          {COLOR_GRADIENTS.map((gradient, rowIndex) => (
                            <div key={rowIndex} className="flex gap-1">
                              {gradient.map((color, colIndex) => (
                                <div
                                  key={colIndex}
                                  className={`w-6 h-6 rounded cursor-pointer hover:scale-110 transition-transform ${
                                    selectedColor === color ? 'ring-2 ring-blue-500 ring-offset-1' : ''
                                  }`}
                                  style={{ backgroundColor: color }}
                                  onClick={() => { setSelectedColor(color); setShowColorPanel(false); }}
                                />
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 文字颜色 */}
                  <div className="relative flex items-center gap-2" ref={textColorPanelRef}>
                    <label className="text-xs font-medium text-gray-600 whitespace-nowrap">文字</label>
                    <button
                      onClick={() => setShowTextColorPanel(!showTextColorPanel)}
                      className="px-2 py-1 border border-gray-300 rounded-lg bg-white hover:border-gray-400 cursor-pointer flex items-center gap-1.5"
                    >
                      <div
                        className="w-5 h-5 rounded border border-gray-300 flex items-center justify-center text-[10px] font-bold"
                        style={{ backgroundColor: selectedTextColor, color: selectedTextColor === '#FFFFFF' ? '#000' : '#FFF' }}
                      >A</div>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showTextColorPanel && (
                      <div className="absolute top-full mt-1 left-0 bg-white border border-gray-300 rounded-lg shadow-xl p-2 z-50">
                        <div className="flex gap-1.5">
                          {TEXT_COLORS.map((item) => (
                            <div
                              key={item.color}
                              className={`w-6 h-6 rounded flex items-center justify-center cursor-pointer hover:scale-110 transition-transform ${
                                selectedTextColor === item.color ? 'ring-2 ring-blue-500 ring-offset-1' : 'border border-gray-300'
                              }`}
                              style={{ backgroundColor: item.color === '#FFFFFF' ? '#F3F4F6' : item.color }}
                              onClick={() => { setSelectedTextColor(item.color); setShowTextColorPanel(false); }}
                            >
                              <span className="text-[10px] font-bold" style={{ color: item.color === '#FFFFFF' ? '#000' : '#FFF' }}>A</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 错误提示 */}
              {error && (
                <div className="mx-5 mt-3 p-2.5 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-600 flex items-center">
                    <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {error}
                  </p>
                </div>
              )}

              {/* 表格编辑区域 */}
              <div className="bg-white px-5 py-3">
                <div className="flex items-baseline mb-2.5">
                  <div className="w-0.5 h-3 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full mr-1.5 self-center"></div>
                  <h3 className="text-xs font-bold text-gray-800">编辑表格内容</h3>
                </div>
                <div className="overflow-auto max-h-[50vh] border border-gray-200 rounded-lg">
                  <table className="w-full border-collapse">
                    <tbody>
                      {tableData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, colIndex) => (
                            <td key={colIndex} className="p-1 border-b border-r border-gray-200 last:border-r-0">
                              <input
                                type="text"
                                value={cell}
                                onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                                placeholder={rowIndex === 0 ? `列${colIndex + 1}` : ''}
                                className={`w-full px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all ${
                                  rowIndex === 0 ? 'font-semibold' : ''
                                }`}
                                style={rowIndex === 0 ? {
                                  backgroundColor: selectedColor,
                                  color: selectedTextColor
                                } : {}}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* 底部按钮 - 精简设计 */}
          <div className="bg-white px-5 py-3 flex justify-end items-center border-t border-gray-200">
            <div className="flex space-x-2">
              <button
                 onClick={handleClose}
                className="px-4 py-1.5 text-sm font-bold border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"

              >
                取消
              </button>
              <button
                onClick={handleInsert}
                disabled={isGenerating || tableData.length === 0}
                className="px-4 py-1.5 text-sm font-bold bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                插入表格
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableDialog;
